import { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, ListChecks, Users, MailCheck, Armchair, Wallet, Store, MessageSquare,
  Globe, Sparkles, Settings, Bell, Menu, X, LogOut, CalendarClock, Heart, Mail, ChevronDown,
  Newspaper, LifeBuoy, Crown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Brand from "@/components/Brand";
import GuidedTour from "@/components/GuidedTour";

const NAV = [
  { section: "Aperçu", items: [{ to: "/app", label: "Tableau de bord", icon: LayoutDashboard, end: true }] },
  { section: "Organiser", items: [
    { to: "/app/tasks", label: "Checklist", icon: ListChecks },
    { to: "/app/guests", label: "Invités", icon: Users },
    { to: "/app/rsvp", label: "RSVP", icon: MailCheck },
    { to: "/app/seating", label: "Plan de table", icon: Armchair },
    { to: "/app/budget", label: "Budget", icon: Wallet },
    { to: "/app/jour-j", label: "Jour J", icon: CalendarClock },
  ]},
  { section: "Prestataires", items: [
    { to: "/app/vendors", label: "Mes prestataires", icon: Store },
    { to: "/marketplace", label: "Marketplace", icon: Heart },
    { to: "/app/messages", label: "Messages", icon: MessageSquare },
  ]},
  { section: "Créer", items: [
    { to: "/app/invitations", label: "Faire-part", icon: Mail },
    { to: "/app/website", label: "Site de mariage", icon: Globe },
    { to: "/app/gazette", label: "Gazette des Mariés", icon: Newspaper, premium: true },
  ]},
  { section: "Plus", items: [
    { to: "/app/ai", label: "Assistant IA", icon: Sparkles },
    { to: "/app/premium", label: "Premium", icon: Crown },
    { to: "/app/support", label: "Support", icon: LifeBuoy },
    { to: "/app/settings", label: "Réglages", icon: Settings },
  ]},
];

const MOBILE = [
  { to: "/app", label: "Accueil", icon: LayoutDashboard, end: true },
  { to: "/app/guests", label: "Invités", icon: Users },
  { to: "/app/seating", label: "Tables", icon: Armchair },
  { to: "/app/budget", label: "Budget", icon: Wallet },
  { to: "/app/ai", label: "IA", icon: Sparkles },
];

function Notifications() {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const load = async () => { try { const { data } = await api.get("/notifications"); setNotes(data); } catch {} };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const unread = notes.filter((n) => !n.read).length;
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <div className="relative">
      <button data-testid="notif-bell" onClick={() => setOpen((o) => !o)}
        className="relative w-10 h-10 rounded-xl hover:bg-stone-100 flex items-center justify-center">
        <Bell className="w-5 h-5 text-stone-600" />
        {unread > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-champagne rounded-full" />}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 card-lux p-3 z-50" data-testid="notif-panel">
          <div className="flex items-center justify-between px-2 pb-2">
            <span className="font-medium text-sm">Notifications</span>
            {unread > 0 && <button onClick={markAll} className="text-xs text-champagne">Tout lire</button>}
          </div>
          <div className="max-h-80 overflow-auto space-y-1">
            {notes.length === 0 && <div className="text-sm text-stone-400 px-2 py-6 text-center">Aucune notification</div>}
            {notes.map((n) => (
              <div key={n.id} className={`px-3 py-2 rounded-lg text-sm ${n.read ? "" : "bg-accent/50"}`}>
                <div className="font-medium text-stone-800">{n.title}</div>
                {n.body && <div className="text-xs text-stone-500">{n.body}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const { user, wedding, logout } = useAuth();
  const [mobileNav, setMobileNav] = useState(false);
  const navigate = useNavigate();
  const w = wedding?.wedding;

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-72 flex-shrink-0 border-r bg-white/70 backdrop-blur h-screen sticky top-0">
        <div className="h-20 flex items-center border-b"><Brand /></div>
        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-6">
          {NAV.map((grp) => (
            <div key={grp.section}>
              <div className="eyebrow px-3 mb-2">{grp.section}</div>
              <div className="space-y-0.5">
                {grp.items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.end} data-testid={`nav-${it.label}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                        isActive ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}>
                    <it.icon className="w-[18px] h-[18px]" /> {it.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="border-t p-3">
          <button onClick={logout} data-testid="logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-stone-600 hover:bg-stone-100 w-full">
            <LogOut className="w-[18px] h-[18px]" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileNav && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMobileNav(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white p-3 overflow-y-auto">
            <div className="flex items-center justify-between h-16"><Brand />
              <button onClick={() => setMobileNav(false)}><X className="w-5 h-5" /></button></div>
            {NAV.map((grp) => (
              <div key={grp.section} className="mb-4">
                <div className="eyebrow px-3 mb-1">{grp.section}</div>
                {grp.items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setMobileNav(false)}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm ${
                      isActive ? "bg-stone-900 text-white" : "text-stone-600"}`}>
                    <it.icon className="w-[18px] h-[18px]" /> {it.label}
                  </NavLink>
                ))}
              </div>
            ))}
            <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 text-sm text-stone-600 w-full">
              <LogOut className="w-[18px] h-[18px]" /> Déconnexion</button>
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-20 sticky top-0 z-40 bg-background/80 backdrop-blur border-b flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMobileNav(true)} data-testid="mobile-menu-btn">
              <Menu className="w-6 h-6" /></button>
            <div className="lg:hidden"><Brand /></div>
            {w && (
              <div className="hidden lg:block">
                <div className="font-serif text-lg text-stone-900">{w.names}</div>
                <div className="text-xs text-stone-400 flex items-center gap-1">
                  <CalendarClock className="w-3 h-3" /> {w.city}</div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Notifications />
            <div className="w-10 h-10 rounded-xl bg-accent flex items-center justify-center font-medium text-sm text-stone-700"
              data-testid="user-avatar">
              {(user?.first_name || "?")[0]}{(user?.last_name || "")[0]}
            </div>
          </div>
        </header>
        <main className="flex-1 px-4 lg:px-8 py-6 lg:py-8 pb-24 lg:pb-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t flex items-center justify-around px-2 py-2">
        {MOBILE.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} data-testid={`mnav-${it.label}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg text-[10px] ${
              isActive ? "text-stone-900" : "text-stone-400"}`}>
            <it.icon className="w-5 h-5" /> {it.label}
          </NavLink>
        ))}
      </nav>

      {/* First-run guided tour (new accounts auto; replay via "Revoir la visite") */}
      <GuidedTour />
    </div>
  );
}
